import { useContext, useEffect, useState } from "react";
import Modal, { ModalProps } from "../components/modal/Modal";
import { SecurityData } from "../utils/helpers/types";
import Input from "../components/input/Input";
import Button from "../components/button/Button";
import LayoutContext from "../context/layout-context";
import {
  createUpdateSecurity,
  getSupportedSecurity
} from "../utils/helpers/data/security";
import Select, { Option } from "../components/select/Select";

interface SecurityUpdateModalProps extends ModalProps {
  issuerCode: string;
  onSuccess: () => void;
  onCancel: () => void;
  securityData: SecurityData | undefined;
}

const SecurityUpdateModal: React.FC<SecurityUpdateModalProps> = ({
  visible,
  onCancel,
  issuerCode,
  onSuccess,
  securityData
}) => {
  const [formData, setFormData] = useState({
    type: "",
    iv: "",
    encryptionKey: "",
    username: "",
    password: "",
    tokenUrl: ""
  });

  const [loading, setLoading] = useState(false);
  const { notify } = useContext(LayoutContext);
  const [supportedTypes, setSupportedTypes] = useState<Option[]>([]);

  useEffect(() => {
    const fetchSupportedTypes = async () => {
      try {
        const response = await getSupportedSecurity();
        if (!response.error) {
          const typeOptions = response.data.map((type: string) => ({
            label: type,
            value: type
          }));
          setSupportedTypes(typeOptions);
        } else {
          console.error("Unable to fetch supported types: ", response.message);
        }
      } catch (err) {
        console.error("Unable to fetch supported types: ", err);
      }
    };

    fetchSupportedTypes();
  }, []);

  useEffect(() => {
    if (visible && securityData) {
      const parsedData = JSON.parse(securityData.data);
      setFormData({
        type: securityData.type,
        iv: parsedData.iv || "",
        encryptionKey: parsedData.encryptionKey || "",
        username: parsedData.username || "",
        password: parsedData.password || "",
        tokenUrl: parsedData.tokenUrl || ""
      });
    }
  }, [visible, securityData]);

  const handleChange = (
    key: string,
    value: string | number | string[] | number[]
  ) => {
    setFormData({ ...formData, [key]: value });
  };

  const handleSubmit = async () => {
    setLoading(true);

    let data: { [key: string]: any } = {};

    if (formData.type === "basic") {
      data = {
        username: formData.username,
        password: formData.password
      };
    } else if (formData.type === "symmetric") {
      data = {
        encryptionKey: formData.encryptionKey,
        iv: formData.iv
      };
    } else if (formData.type === "oauth") {
      data = {
        username: formData.username,
        password: formData.password,
        tokenUrl: formData.tokenUrl
      };
    }

    const requestData = {
      type: formData.type,
      data: JSON.stringify(data)
    };

    try {
      const response = await createUpdateSecurity(issuerCode, requestData);
      if (!response.error) {
        notify("success", "Security updated successfully");
        onSuccess();
        onCancel();
      } else {
        notify("error", response.message || "Unknown error occurred");
      }
    } catch (err: any) {
      console.error("Unable to update security data: ", err);
      notify(
        "error",
        err.response?.responseMessage || err.message || "Unknown error occurred"
      );
    }
    setLoading(false);
  };

  return (
    <Modal visible={visible} cancel={onCancel} size="small">
      <form
        onSubmit={e => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <div className="input-group">
          <span className="question">Type</span>
          <Select
            options={supportedTypes}
            onSelect={selected => handleChange("type", selected)}
            placeholder="Select a type"
            value={formData.type}
            responsive
          />
        </div>

        {formData.type === "basic" && (
          <>
            <div className="input-group">
              <span className="question">Username</span>
              <Input
                value={formData.username}
                onChange={value => handleChange("username", value)}
                placeholder="Enter username"
                required
              />
            </div>
            <div className="input-group">
              <span className="question">Password</span>
              <Input
                value={formData.password}
                onChange={value => handleChange("password", value)}
                placeholder="Enter password"
                type="password"
                required
              />
            </div>
          </>
        )}
        {formData.type === "symmetric" && (
          <>
            <div className="input-group">
              <span className="question">Initial Vector</span>
              <Input
                value={formData.iv}
                onChange={value => handleChange("iv", value)}
                placeholder="Enter initial vector"
                required
              />
            </div>
            <div className="input-group">
              <span className="question">Encryption Key</span>
              <Input
                value={formData.encryptionKey}
                onChange={value => handleChange("encryptionKey", value)}
                placeholder="Enter encryption key"
                required
              />
            </div>
          </>
        )}
        {formData.type === "oauth" && (
          <>
            <div className="input-group">
              <span className="question">Username</span>
              <Input
                value={formData.username}
                onChange={value => handleChange("username", value)}
                placeholder="Enter username"
                required
              />
            </div>
            <div className="input-group">
              <span className="question">Password</span>
              <Input
                value={formData.password}
                onChange={value => handleChange("password", value)}
                placeholder="Enter password"
                required
              />
            </div>

            <div className="input-group">
              <span className="question">Token url</span>
              <Input
                value={formData.tokenUrl}
                onChange={value => handleChange("tokenUrl", value)}
                placeholder="Enter tokenUrl"
                required
              />
            </div>
          </>
        )}

        <div className="flex spaced end vertical-margin">
          <Button buttonType="button" onClick={onCancel}>
            Cancel
          </Button>
          <Button buttonType="submit" loading={loading}>
            Update Security
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default SecurityUpdateModal;
